import { useEffect, useState, useCallback } from 'react';
import {
  BookOpen, ExternalLink, Save, RotateCcw, RefreshCw, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('casa_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function authedFetch(path, init = {}) {
  return fetch(`${API_URL}${path}`, {
    ...init,
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
}


export function DocumentationPanel() {
  const [blocks, setBlocks] = useState([]);
  const [externalKb, setExternalKb] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authedFetch('/api/admin/docs');
      if (res.ok) {
        const data = await res.json();
        setBlocks(data.blocks || []);
        setExternalKb(data.external_kb_url || '');
      }
    } catch (_e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (key, title, body) => {
    const res = await authedFetch(`/api/admin/docs/${key}`, {
      method: 'PUT', body: JSON.stringify({ title, body }),
    });
    if (res.ok) load();
  };

  const handleReset = async (key) => {
    const res = await authedFetch(`/api/admin/docs/${key}/reset`, { method: 'POST' });
    if (res.ok) load();
  };

  return (
    <div className="space-y-4" data-testid="docs-panel">
      {/* External Knowledge Base */}
      <div className="bg-[#111022] border border-white/10 rounded-xl p-5 flex items-start justify-between gap-3 flex-wrap" data-testid="docs-external-kb">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#27D7FF]/15 text-[#27D7FF] flex-shrink-0">
            <BookOpen className="w-4 h-4" />
          </div>
          <div>
            <p className="text-[#F4F2FF] font-semibold">External Knowledge Base</p>
            <p className="text-xs text-[#A7A3BA] mt-0.5">
              Public Casa de Candy KB — bookmark, share with new staff, link from training material.
            </p>
            <p className="text-xs text-[#FFB6D8] mt-1 break-all">{externalKb || '—'}</p>
          </div>
        </div>
        <a
          href={externalKb}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 rounded-lg bg-[#27D7FF]/15 text-[#27D7FF] text-xs flex items-center gap-1 hover:bg-[#27D7FF]/25"
          data-testid="docs-kb-open"
        >
          <ExternalLink className="w-3 h-3" /> Open KB
        </a>
      </div>

      {/* Internal seed blocks */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-[#A7A3BA]" />
          <p className="text-xs text-[#A7A3BA]">
            Internal docs · editable. Reset any block to its seed copy at any time.
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs px-2 py-1 rounded-md bg-[#0B0A12] text-[#A7A3BA] hover:text-[#F4F2FF] flex items-center gap-1"
          data-testid="docs-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="space-y-3" data-testid="docs-block-list">
        {blocks.map((b) => (
          <DocBlockEditor
            key={b.key}
            block={b}
            onSave={handleSave}
            onReset={handleReset}
          />
        ))}
      </div>
    </div>
  );
}


function DocBlockEditor({ block, onSave, onReset }) {
  const [title, setTitle] = useState(block.title);
  const [body, setBody] = useState(block.body);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setTitle(block.title);
    setBody(block.body);
  }, [block.title, block.body]);

  const dirty = title !== block.title || body !== block.body;
  const isDefault =
    title.trim() === (block.default_title || '').trim() &&
    body.trim() === (block.default_body || '').trim();

  const save = async () => {
    setSaving(true);
    await onSave(block.key, title.trim(), body);
    setSavedAt(Date.now());
    setSaving(false);
  };

  const reset = async () => {
    setSaving(true);
    await onReset(block.key);
    setSaving(false);
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#111022] p-4" data-testid={`docs-block-${block.key}`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="flex-1 bg-transparent text-[#F4F2FF] font-semibold text-base focus:outline-none border-b border-transparent focus:border-[#FF4DA6]/40 pb-1 min-w-[200px]"
          data-testid={`docs-block-${block.key}-title`}
        />
        <span className={`text-[10px] uppercase tracking-wider ${isDefault ? 'text-[#A7A3BA]' : 'text-[#FFB6D8]'}`}>
          {isDefault ? 'Seed' : 'Customised'}
        </span>
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={Math.min(14, Math.max(5, body.split('\n').length + 1))}
        className="w-full text-sm bg-[#0B0A12] border border-white/10 rounded-lg px-3 py-2 text-[#F4F2FF] focus:outline-none focus:border-[#FF4DA6]/50 leading-relaxed font-mono"
        data-testid={`docs-block-${block.key}-body`}
      />
      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <p className="text-xs text-[#A7A3BA]">
          {block.updated_at ? `Updated ${new Date(block.updated_at).toLocaleDateString()}` : 'Never edited'}
        </p>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-xs text-green-400">Saved.</span>}
          <button
            type="button"
            onClick={reset}
            disabled={saving || isDefault}
            className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-[#A7A3BA] hover:text-[#F4F2FF] flex items-center gap-1 disabled:opacity-40"
            data-testid={`docs-block-${block.key}-reset`}
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#27D7FF] text-[#07060B] font-medium flex items-center gap-1 disabled:opacity-40"
            data-testid={`docs-block-${block.key}-save`}
          >
            <Save className="w-3 h-3" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


export default DocumentationPanel;
